if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.hiddenInput = this.querySelector('input[type="hidden"][data-variant-id]');
      this.variantData = JSON.parse(this.querySelector('[data-variant-json]')?.textContent || '[]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.currency = window.Shopify?.currency?.active || 'EUR';
      this.locale = document.documentElement.lang || 'en';
      this.hideTrailingZeros = this.dataset.hideTrailingZeros === 'true';
      this.referencePrice = Number(this.variantData[0]?.price || this.currentVariant()?.price || 0);

      this.querySelectorAll('.variant-dropdown').forEach(dropdown => this.setupDropdown(dropdown, signal));
      this.addEventListener('change', event => {
        if (!event.target.matches('.variant-option__input')) return;
        const productUrl = event.target.dataset.productUrl;
        if (productUrl) {
          window.location.assign(productUrl);
          return;
        }
        this.updateSelection(event.target);
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.closeAll();
      }, { signal });
      this.updatePriceAdjustments(this.currentVariant());
      this.updateAvailableOptions();
    }

    setupDropdown(dropdown, signal) {
      const summary = dropdown.querySelector(':scope > summary');
      summary?.addEventListener('click', event => {
        event.preventDefault();
        dropdown.open ? this.closeDropdown(dropdown) : this.openDropdown(dropdown);
      }, { signal });
      dropdown.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !dropdown.open) return;
        event.preventDefault();
        this.closeDropdown(dropdown, true);
      }, { signal });
      dropdown.addEventListener('pointerdown', event => {
        if (event.target === dropdown) this.closeDropdown(dropdown, true);
      }, { signal });
    }

    openDropdown(dropdown) {
      this.closeAll(dropdown);
      dropdown.open = true;
      const panel = dropdown.querySelector('.variant-dropdown__panel');
      if (!panel || this.reducedMotion) return;
      const mobile = matchMedia('(max-width: 749px)').matches;
      panel.animate([
        { opacity: 0, transform: `translateY(${mobile ? '12px' : '-5px'}) scale(.99)` },
        { opacity: 1, transform: 'translateY(0) scale(1)' }
      ], { duration: 170, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' });
    }

    closeDropdown(dropdown, restoreFocus = false) {
      if (!dropdown.open) return;
      const panel = dropdown.querySelector('.variant-dropdown__panel');
      const summary = dropdown.querySelector(':scope > summary');
      if (!panel || this.reducedMotion) {
        dropdown.open = false;
        if (restoreFocus) summary?.focus();
        return;
      }
      const mobile = matchMedia('(max-width: 749px)').matches;
      const animation = panel.animate([
        { opacity: 1, transform: 'translateY(0) scale(1)' },
        { opacity: 0, transform: `translateY(${mobile ? '8px' : '-3px'}) scale(.995)` }
      ], { duration: 115, easing: 'cubic-bezier(.4, 0, 1, 1)', fill: 'both' });
      animation.finished.then(() => {
        dropdown.open = false;
        if (restoreFocus) summary?.focus();
      }).catch(() => {});
    }

    closeAll(exception) {
      this.querySelectorAll('.variant-dropdown[open]').forEach(dropdown => {
        if (dropdown !== exception) this.closeDropdown(dropdown);
      });
    }

    updateSelection(input) {
      const dropdown = input.closest('.variant-dropdown');
      const option = input.closest('.variant-picker__option');
      const template = input.closest('.variant-option')?.querySelector('.variant-option__summary-template');
      const summaryContent = dropdown?.querySelector('.variant-dropdown__selected');
      if (template && summaryContent) summaryContent.replaceChildren(template.content.cloneNode(true));
      const selectedValue = option?.querySelector('[data-option-selected]');
      if (selectedValue) selectedValue.textContent = input.value;

      const selectedOptions = [...this.querySelectorAll('.variant-option__input:checked')].map(option => option.value);
      const variant = this.variantData.find(item => item.options.every((value, index) => value === selectedOptions[index]));
      if (variant && this.hiddenInput) this.hiddenInput.value = variant.id;
      this.updatePriceAdjustments(variant);
      this.updateAvailableOptions();
      this.animateSelection(input);
      if (dropdown) this.closeDropdown(dropdown, true);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updateAvailableOptions() {
      const chosen = [...this.querySelectorAll('.variant-picker__option')].map(field => (
        field.querySelector('.variant-option__input:checked')?.value || ''
      ));
      this.querySelectorAll('.variant-option__input').forEach(input => {
        const index = Number(input.closest('[data-option-position]')?.dataset.optionPosition || 1) - 1;
        const candidate = [...chosen];
        candidate[index] = input.value;
        const variant = this.variantData.find(item => item.options.every((value, i) => value === candidate[i]));
        // Respect Shopify's variant availability instead of raw inventory quantity;
        // untracked inventory can legitimately contain zero available units.
        const unavailable = !variant || variant.available === false;
        input.disabled = unavailable;
        input.closest('.variant-option, .variant-swatch')?.classList.toggle('is-unavailable', unavailable);
      });
    }

    currentVariant() {
      const variantId = Number(this.hiddenInput?.value);
      return this.variantData.find(variant => Number(variant.id) === variantId) || this.variantData[0];
    }

    updatePriceAdjustments(activeVariant) {
      if (!activeVariant) return;
      const selectedOptions = [...this.querySelectorAll('.variant-picker__option')].map(option => (
        option.querySelector('.variant-option__input:checked')?.value || ''
      ));

      this.querySelectorAll('.variant-option__input').forEach(input => {
        const optionPosition = Number(input.closest('[data-option-position]')?.dataset.optionPosition || 0);
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionPosition - 1] = input.value;
        const candidateVariant = this.variantData.find(variant => (
          variant.options.every((value, index) => value === candidateOptions[index])
        ));
        const badge = input.closest('.variant-option, .variant-swatch')?.querySelector('[data-variant-price-adjustment]');
        if (!badge) return;
        if (this.dataset.showPriceAdjustments === 'false') {
          badge.hidden = true;
          return;
        }
        const adjustment = candidateVariant ? Number(candidateVariant.price) - this.referencePrice : 0;
        const nextText = this.formatAdjustment(adjustment);
        const hasChanged = badge.textContent !== nextText;
        badge.hidden = !candidateVariant || adjustment === 0;
        badge.textContent = nextText;
        if (candidateVariant && adjustment !== 0 && hasChanged) this.animatePriceAdjustment(badge);
      });
    }

    animatePriceAdjustment(badge) {
      if (this.reducedMotion) return;
      badge.getAnimations().forEach(animation => animation.cancel());
      badge.animate([
        { opacity: .2, transform: 'translateY(2px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], {
        duration: 220,
        easing: 'cubic-bezier(.22, 1, .36, 1)'
      });
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'symbol',
          minimumFractionDigits: this.hideTrailingZeros ? 0 : 2,
          maximumFractionDigits: 2
        }).format(Number(cents) / 100);
      } catch {
        return `${(Number(cents) / 100).toFixed(2)} ${this.currency}`;
      }
    }

    formatAdjustment(adjustment) {
      if (!adjustment) return '';
      const amount = this.formatMoney(Math.abs(adjustment));
      return adjustment > 0 ? `+${amount}` : (this.dataset.savingTemplate || 'Save [amount]').replace('[amount]', amount);
    }

    animateSelection(input) {
      const label = input.nextElementSibling;
      if (!label || this.reducedMotion) return;
      const imageTextSwatch = label.closest('.variant-swatch--image_swatch_text');
      if (imageTextSwatch) {
        const media = label.querySelector('.variant-swatch__media');
        const copy = label.querySelector('.variant-swatch__copy');
        [media, copy].forEach(element => element?.getAnimations().forEach(animation => animation.cancel()));
        media?.animate([
          { opacity: .68 },
          { opacity: 1 }
        ], {
          duration: 240,
          easing: 'cubic-bezier(.22, 1, .36, 1)'
        });
        copy?.animate([
          { opacity: .35, transform: 'translateY(3px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], {
          duration: 300,
          easing: 'cubic-bezier(.22, 1, .36, 1)'
        });
        imageTextSwatch.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        return;
      }
      label.getAnimations().forEach(animation => animation.cancel());
      label.animate([
        { opacity: .62, transform: 'scale(.985)' },
        { opacity: 1, transform: 'scale(1)' }
      ], {
        duration: 190,
        easing: 'cubic-bezier(.22, 1, .36, 1)'
      });
    }

    disconnectedCallback() { this.abortController?.abort(); }
  });
}

if (!customElements.get('product-size-guide')) {
  customElements.define('product-size-guide', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.dialog = this.querySelector('[data-size-guide-dialog]');
      if (!this.dialog) return;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const { signal } = this.abortController;
      this.guideList = this.dialog.querySelector('.product-size-guide__list');
      this.guideProgress = this.dialog.querySelector('[data-size-guide-progress]');
      this.guideProgressThumb = this.dialog.querySelector('[data-size-guide-progress-thumb]');
      this.querySelectorAll('[data-size-guide-open]').forEach(button => button.addEventListener('click', () => this.open(button), { signal }));
      this.dialog.querySelectorAll('[data-size-guide-close]').forEach(button => button.addEventListener('click', () => this.close(), { signal }));
      this.dialog.querySelectorAll('[data-size-guide-option]').forEach(button => button.addEventListener('click', () => this.selectOption(button), { signal }));
      this.addEventListener('variant:change', () => this.syncActiveCard(), { signal });
      this.guideList?.addEventListener('scroll', () => this.updateScrollProgress(), { passive: true, signal });
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.updateScrollProgress());
      if (this.guideList) this.resizeObserver.observe(this.guideList);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'Tab') window.ThemeComponents?.focus?.trapTab(event, this.dialog);
      }, { signal });
    }

    open(launcher) {
      if (!this.dialog || this.dialog.open) return;
      this.returnFocus = launcher || document.activeElement;
      try { this.dialog.showModal(); }
      catch { this.dialog.setAttribute('open', ''); }
      document.body.classList.add('product-size-guide-open');
      requestAnimationFrame(() => {
        this.dialog.classList.add('is-open');
        this.dialog.querySelector('[data-size-guide-close]')?.focus({ preventScroll: true });
        this.syncActiveCard();
        this.updateScrollProgress();
      });
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.classList.remove('is-open');
      const finish = () => {
        if (!this.dialog?.open) return;
        this.dialog.close();
        document.body.classList.remove('product-size-guide-open');
        window.ThemeComponents?.focus?.restore(this.returnFocus);
      };
      window.setTimeout(finish, this.reducedMotion ? 0 : 300);
    }

    selectOption(button) {
      const optionName = button.dataset.optionName;
      const optionValue = button.dataset.optionValue;
      const picker = this.querySelector('variant-picker');
      const input = [...(picker?.querySelectorAll('.variant-option__input') || [])].find(candidate => (
        candidate.name === `options[${optionName}]` && candidate.value === optionValue
      ));
      if (!input || input.disabled) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncActiveCard();
      this.close();
    }

    syncActiveCard() {
      const picker = this.querySelector('variant-picker');
      const cards = [...(this.dialog?.querySelectorAll('[data-size-guide-option]') || [])];
      let activeCard = null;
      cards.forEach(button => {
        const input = [...(picker?.querySelectorAll('.variant-option__input') || [])].find(candidate => (
          candidate.name === `options[${button.dataset.optionName}]` && candidate.value === button.dataset.optionValue
        ));
        const active = Boolean(input?.checked);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        if (active) activeCard = button;
      });
      const referencePrice = Number(activeCard?.dataset.variantPrice || 0);
      cards.forEach(button => {
        const badge = button.querySelector('[data-size-guide-price-adjustment]');
        if (!badge || !referencePrice) return;
        const adjustment = Number(button.dataset.variantPrice || 0) - referencePrice;
        badge.hidden = adjustment === 0;
        badge.textContent = picker?.formatAdjustment(adjustment) || '';
      });
    }

    updateScrollProgress() {
      if (!this.guideList || !this.guideProgress || !this.guideProgressThumb) return;
      const maximumScroll = Math.max(0, this.guideList.scrollWidth - this.guideList.clientWidth);
      const overflow = maximumScroll > 1;
      this.guideProgress.hidden = !overflow;
      if (!overflow) return;
      const visibleRatio = this.guideList.clientWidth / this.guideList.scrollWidth;
      const thumbWidth = Math.max(18, Math.min(100, visibleRatio * 100));
      const progress = Math.max(0, Math.min(1, this.guideList.scrollLeft / maximumScroll));
      const left = progress * (100 - thumbWidth);
      this.guideProgress.style.setProperty('--size-guide-progress-width', `${thumbWidth}%`);
      this.guideProgress.style.setProperty('--size-guide-progress-left', `${left}%`);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
    }
  });
}
